<script setup lang="ts">
import {computed, onMounted, PropType, ref, watch} from "vue";
import {UploaderI18n} from "../types/I18n";
import {UploadFile} from "../types/UploadFile";
import {GooglePickerUrls} from "../types/GooglePickerUrls";
import {MediaItem, MediaType, VideoProcessingStatus} from "../types/GooglePhotosTypes";
import PagingBar from "./PagingBar.vue";
import GoogleAccountSelector from "./GoogleAccountSelector.vue";
import QRCode from "./QRCode.vue";

const props = defineProps({
    i18n: {
        type: Object as PropType<UploaderI18n>,
        required: true
    },
    googlePickerUrls: {
        type: Object as PropType<GooglePickerUrls>,
        require: true
    },
    doResize: Boolean,
    maxImageSize: Number
})

const emit = defineEmits(['addFileCallback'])

const session = ref(null)
const responseError = ref(null)
const imagesData = ref({mediaItems: [], pageSize: 5})
const currentPageNumber = ref(0)
const doDownloadResizedImage = ref( 0)
doDownloadResizedImage.value = JSON.parse(localStorage.getItem('eg-fileupload-dodownloadresizedimages'))
watch(doDownloadResizedImage, (newVal: boolean) => {
    localStorage.setItem('eg-fileupload-dodownloadresizedimages', JSON.stringify(newVal))
})
const pagingData = computed(() => {
    if (!imagesData.value.mediaItems) return

    const pageSize = imagesData.value.pageSize
    const pagesCount = Math.ceil(imagesData.value.mediaItems.length / pageSize)
    const currentPageContent = imagesData.value.mediaItems.slice(currentPageNumber.value*pageSize, (currentPageNumber.value+1)*pageSize)

    // load the missing thumbnails
    currentPageContent.forEach((mediaItem: MediaItem) => {
        if (!mediaItem.imageBlobThumbnailReqested) {
            mediaItem.imageBlobThumbnailReqested = true
            if (mediaItem.type === MediaType.photo || mediaItem.type === MediaType.video) {
                getImage(mediaItem).then((blob) => {
                    mediaItem.imageBlobThumbnail = URL.createObjectURL(blob)
                })
            }
        }
    })

    return  {
        pagesCount,
        currentPage: currentPageNumber.value,
        mediaItems: currentPageContent
    }
})

let mounted = false;
onMounted(() => {
    gameLoop(false)
    mounted = true
})

function goToPage(index: bigint) {
    currentPageNumber.value = index
}

function addGoogleAccountToUrl(url: string): string {
    return url + '&id=' + activeGoogleAccount.value
}

let pollTimeout: any = null
const gameLoop = async (doNewSession: boolean) => {
    if (activeGoogleAccount.value === null) return
    responseError.value = null
    session.value = null
    imagesData.value = {mediaItems: [], pageSize: 5}
    if (clearTimeout) clearTimeout(pollTimeout)

    try {
        const url = addGoogleAccountToUrl(doNewSession ? props.googlePickerUrls.googlePickerUrlNewSession : props.googlePickerUrls.googlePickerUrlGetSession);
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            }
        });

        const responseData = await response.json();

        if (responseData["error"]) {
            console.log("Encountered error while fetching data from: ", responseData)
            responseError.value = responseData.error
            return
        }

        session.value = responseData

        if (responseData.mediaItemsSet) {
            listImages()
            return
        }
    } catch (e) {
        console.log('Error while fetching data', e);
    }
    const pollForImages = () => {
        if (clearTimeout) clearTimeout(pollTimeout)
        pollTimeout = setTimeout(() => {
            fetch(addGoogleAccountToUrl(props.googlePickerUrls.googlePickerUrlGetSession))
                .then((response) => response.json())
                .then((responseData) => {
                    session.value = responseData
                    if (responseData.mediaItemsSet) {
                        listImages()
                    } else {
                        pollForImages()
                    }
                })
        }, 5000) // default to poll every 5 seconds
    }
    pollForImages()
}

const getImage = async (mediaItem: MediaItem, sizeParams:string = "=w128-h128") => {
    const response = await fetch(addGoogleAccountToUrl(props.googlePickerUrls.googlePickerUrlGetImage), {
        method: 'POST',
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
        },
        body: `baseUrl=${mediaItem.mediaFile.baseUrl}${sizeParams}`
    })
    return await response.blob()
}

const listImages = () => {
    fetch(addGoogleAccountToUrl(props.googlePickerUrls.googlePickerUrlGetImages))
        .then((response) => response.json())
        .then((responseData) => {
            imagesData.value = responseData
        })
}

const startNewSession = () => {
    gameLoop(true)
}

let activeGoogleAccount = ref(null)
const setActiveGoogleAccount = async (accountId: bigint) => {
    if (accountId !== activeGoogleAccount.value) {
        activeGoogleAccount.value = accountId
        if (mounted) {
            await gameLoop(false)
        }
    }
}

const addSelectedFilesToUpload = () => {

    imagesData.value.mediaItems.forEach(async (mediaItem: MediaItem) => {
        let queryParameter = ''

        if (mediaItem.type === MediaType.photo) {
            queryParameter = '=d'
            if (doDownloadResizedImage) {
                queryParameter = `=w${props.maxImageSize}-h${props.maxImageSize}`
            }
        }
        if (mediaItem.type === MediaType.video && mediaItem.mediaFile.mediaFileMetadata.videoMetadata.processingStatus === VideoProcessingStatus.READY) {
            queryParameter = '=dv'
        }

        if (queryParameter === '') return

        const blob = await getImage(mediaItem, queryParameter)
        const uploadFile: UploadFile = {
            localFile: new File([blob], mediaItem.mediaFile.filename, {type: mediaItem.mediaFile.mimeType}),
            name: mediaItem.mediaFile.filename,
            doResize: false
        }
        emit('addFileCallback', uploadFile)
    })
}

</script>

<template>
    <div>
        <label for="dodownloadresizedimages">{{i18n.COM_EVENTGALLERY_EVENT_UPLOAD_DO_DOWNLOAD_RESIZED_IMAGES_LABEL}}:</label>
        <input type="checkbox"
id="dodownloadresizedimages"
name="dodownloadresizedimages"
v-model="doDownloadResizedImage"/>
    </div>
    <div class="google-photos-toolbar">
        <button v-if="activeGoogleAccount" class="btn btn-warning" @click="startNewSession()">{{ i18n.COM_EVENTGALLERY_EVENT_UPLOAD_GOOGLE_PHOTOS_START_NEW_SELECTION }}</button>
        <GoogleAccountSelector v-if="imagesData.mediaItems.length === 0"
                               :i18n="i18n"
                               :google-picker-url-get-accounts="googlePickerUrls.googlePickerUrlGetAccounts"
                               :google-picker-url-manage-accounts="googlePickerUrls.googlePickerUrlManageAccounts"
                               @setActiveGoogleAccount="setActiveGoogleAccount"/>
    </div>
    <template v-if="activeGoogleAccount">
        <div v-if="!session && !responseError">{{ i18n.COM_EVENTGALLERY_EVENT_UPLOAD_GOOGLE_PHOTOS_LOADING_SESSION }}</div>
        <template v-else>
            <div v-if="imagesData.mediaItems.length === 0">


                <div v-if="responseError" class="error">{{ responseError }}</div>

                <div v-if="responseError == null" class="google-photos-picker-urls">
                    <h1>{{i18n.COM_EVENTGALLERY_EVENT_UPLOAD_GOOGLE_PHOTOS_PICKER_HEADLINE}}</h1>

                    <p v-if="session?.pollingConfig">{{i18n.COM_EVENTGALLERY_EVENT_UPLOAD_GOOGLE_PHOTOS_PICKER_SESSION_RUNTIME.replace('%s', Math.round(parseInt(session.pollingConfig.timeoutIn)))}}</p>

                    <div v-if="session?.pickerUri" class="links">
                        <a target="_blank" class="" :href="session.pickerUri">{{ i18n.COM_EVENTGALLERY_EVENT_UPLOAD_GOOGLE_PHOTOS_PICKER_LINK_DESC }}</a>
                        <a target="_blank" class="" :href="session.pickerUri">
                            <QRCode :text="session.pickerUri" :alt="i18n.COM_EVENTGALLERY_EVENT_UPLOAD_GOOGLE_PHOTOS_PICKER_LINK_DESC"/>
                        </a>
                    </div>

                    <div class="">
                        <p class="">{{ i18n.COM_EVENTGALLERY_EVENT_UPLOAD_GOOGLE_PHOTOS_PICKER_RELOAD_DESC }}</p>
                    </div>
                </div>
            </div>
            <div v-if="imagesData.mediaItems.length > 0" id="google-picker-result">
                <h3>{{i18n.COM_EVENTGALLERY_EVENT_UPLOAD_GOOGLE_PHOTOS_PICKER_SELECTED_IMAGES_DESC.replace('%s', imagesData.mediaItems.length)}}</h3>
                <PagingBar :pagingData="pagingData" @goToPage="goToPage"/>

                <div class="image-container">
                    <div v-for="value in pagingData?.mediaItems" :key="value.id" :value="value.id">
                        <img :src="value.imageBlobThumbnail || 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw=='" :alt="value.mediaFile.filename"/>
                    </div>
                </div>

                <PagingBar :pagingData="pagingData" @goToPage="goToPage"/>

                <button class="btn btn-primary" @click="addSelectedFilesToUpload">{{i18n.COM_EVENTGALLERY_EVENT_UPLOAD_GOOGLE_PHOTOS_PICKER_UPLOAD_BUTTON_LABEL.replace('%s', imagesData.mediaItems.length)}}</button>
            </div>
        </template>
    </template>
</template>

<style scoped lang="less">
    a {
        cursor: pointer;
    }

    .google-photos-toolbar {
        display: flex;
        margin-bottom: 20px;
    }

    .google-photos-picker-urls {
        h1,p {
            text-align: center;
        }
    }

    .links {
        display: flex;
        align-items: baseline;
        justify-content: space-evenly;
    }

    .image-container {
        display: flex;
        flex-wrap: wrap;
        align-items: baseline;
        justify-content: space-between;

        div {
            height: 128px;
            width: 128px;
            margin: 10px;
        }
        img {
            border-radius: 5px;
            max-width: 100%;
            max-height: 100%;
            object-fit: contain;
        }
    }

    .error {
        border: 1px solid red;
        padding: 10px;
    }
</style>